import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  ChevronRight,
  Clock,
  MapPin,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { formatDateForDisplay } from '@/lib/utils/date';
import { fetchActivityImage } from '@/lib/fetchActivityImage';

interface Activity {
  id: string;
  sport: string;
  title?: string;             // ← added
  date: string;
  time: string;
  venue_name: string;
  venue_city: string;
  // for 1-on-1
  opponent?: {
    name: string;
    image: string;
  };
  // for group
  participants?: string[];
  participantsCount?: number;
}

// Extract leading emoji
const extractEmoji = (str: string) => {
  const m = str.match(/^[\p{Emoji}\p{Extended_Pictographic}]/u);
  return m ? m[0] : '🏅';
};

const cleanSportName = (str: string) =>
  str.replace(/^[\p{Emoji}\p{Extended_Pictographic}]+\s*/u, '');

export default function UpcomingActivities() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [images, setImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1) Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Authentication required');

        const { data: me, error: meErr } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        if (meErr || !me) throw meErr || new Error('User record not found');

        const today = new Date().toISOString().split('T')[0];

        // 2) Fetch individual future matches
        const { data: indData, error: indErr } = await supabase
          .from('activities')
          .select(`
            id,
            sport,
            date,
            time,
            venue_name,
            venue_city,
            player_1:player_1_id(id, full_name, avatar_url),
            player_2:player_2_id(id, full_name, avatar_url)
          `)
          .or(`player_1_id.eq.${me.id},player_2_id.eq.${me.id}`)
          .eq('status','confirmed')
          .gte('date', today);
        if (indErr) throw indErr;

        const individual: Activity[] = (indData || []).map(a => {
          const isP1 = a.player_1.id === me.id;
          const opp = isP1 ? a.player_2 : a.player_1;
          return {
            id: `ind-${a.id}`,
            sport: a.sport,
            date: a.date,
            time: a.time,
            venue_name: a.venue_name,
            venue_city: a.venue_city,
            opponent: {
              name: opp.full_name,
              image: opp.avatar_url || '/images/default-avatar.png',
            },
          };
        });

        // 3) Fetch upcoming group activities
        const { data: grpData, error: grpErr } = await supabase
          .from('group_activities')
          .select('id, sport, title, group_date, group_time, venue_name, venue_city')
          .gte('group_date', today)
          .neq('status','cancelled');
        if (grpErr) throw grpErr;

        // 4) For each group, fetch its participants
        const groups: Activity[] = await Promise.all(
          (grpData || []).map(async g => {
            const { data: parts } = await supabase
              .from('group_activity_participants')
              .select('user_id')
              .eq('group_activity_id', g.id);

            const avatars = await Promise.all(
              (parts || []).map(async p => {
                const { data: u } = await supabase
                  .from('users')
                  .select('avatar_url')
                  .eq('id', p.user_id)
                  .single();
                return u?.avatar_url || '/images/default-avatar.png';
              })
            );

            return {
              id: `grp-${g.id}`,
              sport: g.sport,
              title: g.title,             // ← show actual title
              date: g.group_date,
              time: g.group_time,
              venue_name: g.venue_name,
              venue_city: g.venue_city,
              participants: avatars,
              participantsCount: avatars.length,
            };
          })
        );

        // 5) Merge & sort
        const combined = [...individual, ...groups]
          .sort((a, b) => {
            const tA = new Date(`${a.date}T${a.time}`).getTime();
            const tB = new Date(`${b.date}T${b.time}`).getTime();
            return tA - tB;
          })
          .slice(0, 3);

        setActivities(combined);

        // 6) Preload one thumbnail per event
        const imgMap: Record<string, string> = {};
        await Promise.all(combined.map(async act => {
          try {
            imgMap[act.id] = await fetchActivityImage(act.sport);
          } catch {
            /* ignore */
          }
        }));
        setImages(imgMap);

      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const goAll = () => navigate('/dashboard/activities');

  const fmtTime = (ts: string) => {
    if (!ts) return '';
    const d = new Date(`1970-01-01T${ts}`);
    return d.toLocaleTimeString('default', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  if (loading) return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse">
      <div className="flex justify-between mb-4">
        <div className="h-6 w-40 bg-dark-300 rounded" />
        <div className="h-6 w-24 bg-dark-300 rounded" />
      </div>
      <div className="space-y-3">
        {[1,2,3].map(i=>(
          <div key={i} className="bg-dark-300 rounded-lg h-20" />
        ))}
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6">
      <div className="flex justify-between mb-4">
        <div className="flex items-center">
          <Calendar className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Upcoming Activities</h3>
        </div>
      </div>
      <div className="text-dark-800 text-center py-4">{error}</div>
    </div>
  );

  if (!activities.length) return null;

  return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6">
      <div className="flex justify-between mb-4">
        <div className="flex items-center">
          <Calendar className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Upcoming Activities</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={goAll}
          className="text-dark-800 hover:text-white"
        >
          View All
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <div className="space-y-3">
        {activities.map(act => (
          <div
            key={act.id}
            className={cn(
              'relative rounded-xl p-5 cursor-pointer transition-all duration-300 overflow-hidden',
              'border border-dark-400 hover:border-gold-400 group shadow-md hover:shadow-xl',
              'bg-gradient-to-br from-dark-300 to-dark-400',
              'transform hover:scale-[1.02] hover:-translate-y-1'
            )}
            onClick={() =>
              navigate('/dashboard/activities', {
                state: { viewMode: act.participants ? 'group' : 'individual' }
              })
            }
          >
            {/* background image */}
            {images[act.id] && (
              <div className="absolute inset-0 opacity-40 group-hover:opacity-60 transition-all duration-500">
                <img
                  src={images[act.id]}
                  alt={act.sport}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 filter brightness-125"
                />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-dark-300/75 via-dark-300/50 to-transparent pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center gap-4">
                {/* Group vs Individual avatar block */}
                {act.participants ? (
                  <div className="flex -space-x-2">
                    {act.participants.slice(0,3).map((url,i) => (
                      <img
                        key={i}
                        src={url}
                        className="w-12 h-12 rounded-full border-2 border-white"
                      />
                    ))}
                    {act.participantsCount! > 3 && (
                      <div className="w-12 h-12 rounded-full bg-black/60 border-2 border-white flex items-center justify-center text-sm text-white">
                        +{act.participantsCount! - 3}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={act.opponent!.image}
                      alt={act.opponent!.name}
                      className="w-20 h-20 rounded-full border-2 border-dark-400 group-hover;border-gold-400 transition-all duration-300 shadow-lg"
                    />
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-black/60 rounded-full border-2 border-dark-400 flex items-center justify-center">
                      {extractEmoji(act.sport)}
                    </div>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h4 className="font-bold !text-white text-xl mb-2 group-hover:text-gold-400 transition-colors duration-300">
                    {act.participants
                      ? act.title
                      : `${cleanSportName(act.sport)} with ${act.opponent!.name}`
                    }
                  </h4>
                  <span className="bg-gold-400 text-gray-900 font-semibold text-sm px-4 py-1.5 rounded-full shadow-md inline-block transform group-hover:scale-105 transition-transform duration-300">
                    {formatDateForDisplay(act.date)}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-4">
                <div className="flex items-center px-4 py-2 rounded-lg shadow-md bg-black/60">
                  <Clock className="h-5 w-5 mr-2 text-gold-400" />
                  <span className="font-medium !text-white">{fmtTime(act.time)}</span>
                </div>
                <div className="flex items-center px-4 py-2 rounded-lg shadow-md bg-black/60">
                  <MapPin className="h-5 w-5 mr-2 text-gold-400" />
                  <span className="truncate font-medium !text-white max-w-[200px]">
                    {[act.venue_name, act.venue_city].filter(Boolean).join(', ')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
